import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getIn, useFormikContext } from 'formik';
import type { FormikValues } from 'formik/dist/types';
import { omit, set } from 'lodash';
import * as Yup from 'yup';

import { ConfirmModal } from '~/components/ConfirmModal';
import {
  ADDRESS_VALIDATION_STATUSES,
  ADDRESS_VALIDATION_TYPE,
  REVERTABLE_STATUSES,
} from '~/components/Contacts/addressValidationStatuses';
import AddressVerificationBadge from '~/components/Contacts/AddressVerificationBadge/AddressVerificationBadge';
import type { Tab } from '~/components/Contacts/UpsertContact/types';
import { useContact } from '~/components/Contacts/UpsertContact/useContact';
import AddressAutocompleteFormik from '~/components/core/AddressAutocomplete/AddressAutocompleteFormik';
import SuburbAutocompleteFormik from '~/components/core/AddressAutocomplete/GbgAddressAutocomplete/SuburbAutocompleteFormik';
import type { GbgAddressRetrieveValidation } from '~/components/core/AddressAutocomplete/GbgAddressAutocomplete/types';
import { isGbgSupportedCountry } from '~/components/core/AddressAutocomplete/GbgAddressAutocomplete/useGbgValidationStatus';
import Text from '~/components/core/TextComponents/Text';
import CountryAutocompleteFormik from '~/components/CountryAutocompleteFormik';
import { StateFieldFormik } from '~/components/GlobalLossLocation';
import useGlobalAddresses from '~/components/hooks/useGlobalAddresses';
import { ErrorIcon } from '~/components/icons';
import TextFieldFormik from '~/components/TextFieldFormik';
import CONTACT_ADDRESS_CHANGE_REASONS from '~/server_shared/generated-types/CONTACT_ADDRESS_CHANGE_REASONS';
import type { ContactFullModel } from '~/server_shared/swagger/data-contracts';
import { getPostalCodeYupTestParams } from '~/Utils/postalCodeUtils';

import ChangeReason from '../ChangeReason';
import { useFieldsValidationRevert } from '../useFieldsValidationRevert';
import { getInitializedValues, getReasonValidationSchema, normalizeOptionalString as norm } from '../utils';

import TabWrapper from './TabWrapper';

const TEXTS = {
  disclaimerTitle: 'Enter the contact’s address to be verified',
  disclaimerBody:
    'Once verified, the address detail fields will be auto-filled. If you edit these fields, they can no longer be verified.',
} as const;

const ADDRESS_TAB_KEY = 'address';

const getFullFieldPath = (fieldId: string) => `${ADDRESS_TAB_KEY}.${fieldId}`;

const FORMIK_ADDRESS_FIELD_IDS = {
  COUNTRY: `country`,
  STREET_ADDRESS_1: `street_address1`,
  STREET_ADDRESS_2: `street_address2`,
  CITY: `city`,
  COUNTY: `county`,
  STATE: `state`,
  ZIPCODE: `zipcode`,
  ADDRESS_VALIDATION: `address_validation`,
  OVERRIDE_REASON: 'override_reason',
  REASON: `reason`,
  EXPLANATION: `explanation`,
} as const;

// street_address2 is excluded: GBG's street2 field is a rare secondary street line
// (almost always empty) — AU/NZ subdwelling info is baked into street_address1.
// country is excluded: changing country already clears all address fields via the
// change handlers below, which triggers the revert indirectly.
const ADDRESS_FIELDS_TO_WATCH = [
  FORMIK_ADDRESS_FIELD_IDS.STREET_ADDRESS_1,
  FORMIK_ADDRESS_FIELD_IDS.CITY,
  FORMIK_ADDRESS_FIELD_IDS.STATE,
  FORMIK_ADDRESS_FIELD_IDS.ZIPCODE,
] as const;

const BUSINESS_TAB_KEY = 'business';
const BUSINESS_FIELD_IDS = [
  'au_nz_business_number',
  'business_legal_name',
  'business_trading_name',
  'au_nz_company_number',
  'business_validation',
] as const;
const BUSINESS_FIELD_IDS_TO_WATCH = [
  'au_nz_business_number',
  'business_legal_name',
  'business_trading_name',
  'au_nz_company_number',
] as const;

/** Returns true when the contact has a validated business (current session or server-side). */
const hasValidatedBusiness = (values: unknown, originalContact?: ContactFullModel): boolean => {
  // Current-session validation stored in formik
  const formikValidation = getIn(values, `${BUSINESS_TAB_KEY}.business_validation`);
  if (formikValidation) return true;

  // Server-side validation from a previous save (any status except not_validated)
  const serverValidation = originalContact?.validations?.find((v) => v.validation_type === 'business');
  if (serverValidation && serverValidation.status !== 'not_validated') {
    const isBusinessDirty = BUSINESS_FIELD_IDS_TO_WATCH.some(
      (field) => norm(getIn(values, `${BUSINESS_TAB_KEY}.${field}`)) !== norm(originalContact?.[field])
    );
    return !isBusinessDirty;
  }

  return false;
};

const useAddressSessionValidation = (): {
  sessionValidation: GbgAddressRetrieveValidation | undefined;
  overrideReasonRequired: boolean;
} => {
  const { values } = useFormikContext();
  const { originalContact } = useContact();
  const validatedInSessionRef = useRef(false);

  const formikValidation = getIn(values, getFullFieldPath(FORMIK_ADDRESS_FIELD_IDS.ADDRESS_VALIDATION));

  let sessionValidation: GbgAddressRetrieveValidation | undefined;

  useEffect(() => {
    if (formikValidation) {
      validatedInSessionRef.current = true;
    }
  }, [formikValidation]);

  if (formikValidation) {
    sessionValidation = {
      validation_type: 'address',
      status: formikValidation.status ?? ADDRESS_VALIDATION_STATUSES.VERIFIED,
      provider: 'gbg',
      validation_details: formikValidation.validation_details ?? null,
    };
  } else {
    const serverStatus = originalContact?.validations?.find(
      (v) => v.validation_type === ADDRESS_VALIDATION_TYPE
    )?.status;
    const wasPreviouslyValidated =
      validatedInSessionRef.current || REVERTABLE_STATUSES.some((status) => status === serverStatus);

    if (wasPreviouslyValidated) {
      const isAddressDirty = ADDRESS_FIELDS_TO_WATCH.some(
        (field) => norm(getIn(values, getFullFieldPath(field))) !== norm(originalContact?.[field])
      );
      if (isAddressDirty) {
        sessionValidation = {
          validation_type: 'address',
          status: ADDRESS_VALIDATION_STATUSES.NOT_VALIDATED,
          provider: 'gbg',
          validation_details: null,
        };
      }
    }
  }

  const effectiveStatus =
    sessionValidation?.status ??
    originalContact?.validations?.find((v) => v.validation_type === ADDRESS_VALIDATION_TYPE)?.status;
  const overrideReasonRequired = !effectiveStatus || !REVERTABLE_STATUSES.some((status) => status === effectiveStatus);

  return { sessionValidation, overrideReasonRequired };
};

interface AddressVerificationHeaderProps {
  sessionValidation?: GbgAddressRetrieveValidation;
}

const AddressVerificationHeader: React.FC<AddressVerificationHeaderProps> = ({ sessionValidation }) => {
  const { originalContact } = useContact();
  const validations = originalContact?.validations;

  return (
    <div className="col-span-full">
      <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.SEMI_BOLD}>
        {TEXTS.disclaimerTitle}
      </Text>
      <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.REGULAR} className="mb-12 mt-2 flex gap-2">
        <ErrorIcon iconColor="currentColor" className="mt-2 shrink-0" />
        {TEXTS.disclaimerBody}
      </Text>
      <AddressVerificationBadge validations={validations} sessionValidation={sessionValidation} />
    </div>
  );
};

const AddressTab: React.FC = () => {
  const { isSubmitting, values, setFieldValue } = useFormikContext();
  const { isCreation, currentCountry, setCurrentCountry, gbgValidationStatus, originalContact } = useContact();

  const isCountryUs = currentCountry === 'US';
  const isAddressValidationEnabled =
    gbgValidationStatus.is_address_validation_enabled && isGbgSupportedCountry(currentCountry);

  const { sessionValidation, overrideReasonRequired } = useAddressSessionValidation();

  const hasAddressContent = ADDRESS_FIELDS_TO_WATCH.some((field) => !!getIn(values, getFullFieldPath(field))?.trim());

  const [pendingCountry, setPendingCountry] = useState<{ value: string } | null>(null);

  const clearAddressFields = useCallback(() => {
    setFieldValue(getFullFieldPath(FORMIK_ADDRESS_FIELD_IDS.STREET_ADDRESS_1), '');
    setFieldValue(getFullFieldPath(FORMIK_ADDRESS_FIELD_IDS.STREET_ADDRESS_2), '');
    setFieldValue(getFullFieldPath(FORMIK_ADDRESS_FIELD_IDS.CITY), '');
    setFieldValue(getFullFieldPath(FORMIK_ADDRESS_FIELD_IDS.COUNTY), '');
    setFieldValue(getFullFieldPath(FORMIK_ADDRESS_FIELD_IDS.STATE), '');
    setFieldValue(getFullFieldPath(FORMIK_ADDRESS_FIELD_IDS.ZIPCODE), '');
    setFieldValue(getFullFieldPath(FORMIK_ADDRESS_FIELD_IDS.OVERRIDE_REASON), '');
  }, [setFieldValue]);

  const clearBusinessFields = useCallback(() => {
    for (const field of BUSINESS_FIELD_IDS) {
      setFieldValue(`${BUSINESS_TAB_KEY}.${field}`, field === 'business_validation' ? null : '');
    }
  }, [setFieldValue]);

  const handleConfirmCountryChange = useCallback(() => {
    if (!pendingCountry) return;
    const newCountry = pendingCountry.value;
    clearAddressFields();
    clearBusinessFields();
    setCurrentCountry(newCountry);
    setFieldValue(getFullFieldPath(FORMIK_ADDRESS_FIELD_IDS.COUNTRY), newCountry);
    setPendingCountry(null);
  }, [pendingCountry, setFieldValue, clearAddressFields, clearBusinessFields, setCurrentCountry]);

  const handleCancelCountryChange = useCallback(() => {
    setPendingCountry(null);
  }, []);

  useFieldsValidationRevert(
    ADDRESS_FIELDS_TO_WATCH,
    getFullFieldPath(FORMIK_ADDRESS_FIELD_IDS.ADDRESS_VALIDATION),
    getFullFieldPath
  );

  const handleCountryChange = (_: unknown, country: string) => {
    if (country === currentCountry) {
      // Just an extra precaution - the change handler shouldn't trigger in such a case
      return;
    }
    if (hasValidatedBusiness(values, originalContact)) {
      // Validated business exists — ask for confirmation before applying the country change
      setPendingCountry({ value: country ?? '' });
      return;
    }
    clearAddressFields();
    setFieldValue(getFullFieldPath(FORMIK_ADDRESS_FIELD_IDS.COUNTRY), country ?? '');
    setCurrentCountry(country);
  };

  return (
    <TabWrapper>
      <div className="mb-32 grid grid-cols-2 gap-20">
        {isAddressValidationEnabled ? <AddressVerificationHeader sessionValidation={sessionValidation} /> : null}
        <div className="col-span-full">
          <CountryAutocompleteFormik
            id={getFullFieldPath(FORMIK_ADDRESS_FIELD_IDS.COUNTRY)}
            label="Country"
            countriesConfigurationKey="contact_location"
            disabled={isSubmitting}
            onChange={handleCountryChange}
          />
        </div>
        <AddressAutocompleteFormik
          className="col-span-full"
          id={getFullFieldPath(FORMIK_ADDRESS_FIELD_IDS.STREET_ADDRESS_1)}
          label="Address 1"
          fullWidth
          disabled={isSubmitting}
          helperText={isCountryUs ? '' : 'Street name & number, P.O. box, C/O, etc.'}
          streetAddress1FieldId={getFullFieldPath(FORMIK_ADDRESS_FIELD_IDS.STREET_ADDRESS_1)}
          streetAddress2FieldId={getFullFieldPath(FORMIK_ADDRESS_FIELD_IDS.STREET_ADDRESS_2)}
          countyFieldId={getFullFieldPath(FORMIK_ADDRESS_FIELD_IDS.COUNTY)}
          countryFieldId={getFullFieldPath(FORMIK_ADDRESS_FIELD_IDS.COUNTRY)}
          zipcodeFieldId={getFullFieldPath(FORMIK_ADDRESS_FIELD_IDS.ZIPCODE)}
          cityFieldId={getFullFieldPath(FORMIK_ADDRESS_FIELD_IDS.CITY)}
          stateFieldId={getFullFieldPath(FORMIK_ADDRESS_FIELD_IDS.STATE)}
          addressValidationFieldId={getFullFieldPath(FORMIK_ADDRESS_FIELD_IDS.ADDRESS_VALIDATION)}
          onAddressValidated={() => setFieldValue(getFullFieldPath(FORMIK_ADDRESS_FIELD_IDS.OVERRIDE_REASON), '')}
        />
        <TextFieldFormik
          className="col-span-full"
          id={getFullFieldPath(FORMIK_ADDRESS_FIELD_IDS.STREET_ADDRESS_2)}
          label="Address 2"
          fullWidth
          disabled={isSubmitting}
          helperText={isCountryUs ? '' : 'Apartment, suite, unit, building name, floor, etc.'}
        />
        <SuburbAutocompleteFormik
          id={getFullFieldPath(FORMIK_ADDRESS_FIELD_IDS.CITY)}
          label="City"
          fullWidth
          disabled={isSubmitting}
          cityFieldId={getFullFieldPath(FORMIK_ADDRESS_FIELD_IDS.CITY)}
          zipcodeFieldId={getFullFieldPath(FORMIK_ADDRESS_FIELD_IDS.ZIPCODE)}
          streetAddress2FieldId={getFullFieldPath(FORMIK_ADDRESS_FIELD_IDS.STREET_ADDRESS_2)}
          stateFieldId={getFullFieldPath(FORMIK_ADDRESS_FIELD_IDS.STATE)}
          countryFieldId={getFullFieldPath(FORMIK_ADDRESS_FIELD_IDS.COUNTRY)}
        />
        <SuburbAutocompleteFormik
          id={getFullFieldPath(FORMIK_ADDRESS_FIELD_IDS.ZIPCODE)}
          label="Postal Code"
          fullWidth
          disabled={isSubmitting}
          cityFieldId={getFullFieldPath(FORMIK_ADDRESS_FIELD_IDS.CITY)}
          zipcodeFieldId={getFullFieldPath(FORMIK_ADDRESS_FIELD_IDS.ZIPCODE)}
          streetAddress2FieldId={getFullFieldPath(FORMIK_ADDRESS_FIELD_IDS.STREET_ADDRESS_2)}
          stateFieldId={getFullFieldPath(FORMIK_ADDRESS_FIELD_IDS.STATE)}
          countryFieldId={getFullFieldPath(FORMIK_ADDRESS_FIELD_IDS.COUNTRY)}
        />
        {isCountryUs ? (
          <TextFieldFormik
            id={getFullFieldPath(FORMIK_ADDRESS_FIELD_IDS.COUNTY)}
            label="County"
            fullWidth
            disabled={isSubmitting}
          />
        ) : null}
        <StateFieldFormik
          id={getFullFieldPath(FORMIK_ADDRESS_FIELD_IDS.STATE)}
          disabled={isSubmitting}
          country={getIn(values, getFullFieldPath(FORMIK_ADDRESS_FIELD_IDS.COUNTRY))}
        />

        {isAddressValidationEnabled && overrideReasonRequired && hasAddressContent ? (
          <TextFieldFormik
            className="col-span-full"
            id={getFullFieldPath(FORMIK_ADDRESS_FIELD_IDS.OVERRIDE_REASON)}
            label="Verification Override Reason"
            fullWidth
            disabled={isSubmitting}
          />
        ) : null}

        {!isCreation ? (
          <div className="col-span-full">
            <ChangeReason
              reasonFieldId={getFullFieldPath(FORMIK_ADDRESS_FIELD_IDS.REASON)}
              explanationFieldId={getFullFieldPath(FORMIK_ADDRESS_FIELD_IDS.EXPLANATION)}
              reasonsDict={CONTACT_ADDRESS_CHANGE_REASONS}
              disabled={isSubmitting}
            />
          </div>
        ) : null}
      </div>
      <ConfirmModal
        isOpen={pendingCountry !== null}
        onClose={handleCancelCountryChange}
        onPrimaryBtnClick={handleConfirmCountryChange}
        title="Change country?"
        contentText="Changing the country will remove the current business data and validation. Are you sure you want to continue?"
        primaryButtonName="Change"
      />
    </TabWrapper>
  );
};

const useAddressTab = (): Tab => {
  // No Formik Context
  const { originalContact, isCreation, gbgValidationStatus } = useContact();
  const { defaultCountry } = useGlobalAddresses({ countriesConfigurationKey: 'contact_location' });

  const getInitialValues = (contact?: ContactFullModel) => {
    const values = {};

    const setInitialValue = (fieldId: string, initialValue: unknown, defaultInitialValue: unknown = '') =>
      set(values, fieldId, initialValue ?? defaultInitialValue);

    setInitialValue(FORMIK_ADDRESS_FIELD_IDS.COUNTRY, contact?.country, isCreation ? defaultCountry : '');
    setInitialValue(FORMIK_ADDRESS_FIELD_IDS.STREET_ADDRESS_1, contact?.street_address1);
    setInitialValue(FORMIK_ADDRESS_FIELD_IDS.STREET_ADDRESS_2, contact?.street_address2);
    setInitialValue(FORMIK_ADDRESS_FIELD_IDS.CITY, contact?.city);
    setInitialValue(FORMIK_ADDRESS_FIELD_IDS.COUNTY, contact?.county);
    setInitialValue(FORMIK_ADDRESS_FIELD_IDS.STATE, contact?.state);
    setInitialValue(FORMIK_ADDRESS_FIELD_IDS.ZIPCODE, contact?.zipcode);
    setInitialValue(FORMIK_ADDRESS_FIELD_IDS.ADDRESS_VALIDATION, null, null);
    setInitialValue(FORMIK_ADDRESS_FIELD_IDS.OVERRIDE_REASON, '');

    if (!isCreation) {
      setInitialValue(FORMIK_ADDRESS_FIELD_IDS.REASON, '');
      setInitialValue(FORMIK_ADDRESS_FIELD_IDS.EXPLANATION, '');
    }

    return values;
  };

  const getValidationSchema = () => {
    const validations = {};

    const addValidation = (fieldId: string, fieldValidation: Yup.AnySchema) =>
      set(validations, fieldId, fieldValidation);

    addValidation(FORMIK_ADDRESS_FIELD_IDS.COUNTRY, Yup.string().required('Required'));
    addValidation(FORMIK_ADDRESS_FIELD_IDS.STREET_ADDRESS_1, Yup.string());
    addValidation(FORMIK_ADDRESS_FIELD_IDS.STREET_ADDRESS_2, Yup.string());
    addValidation(FORMIK_ADDRESS_FIELD_IDS.CITY, Yup.string());
    addValidation(FORMIK_ADDRESS_FIELD_IDS.COUNTY, Yup.string());
    addValidation(FORMIK_ADDRESS_FIELD_IDS.STATE, Yup.string());
    addValidation(
      FORMIK_ADDRESS_FIELD_IDS.ZIPCODE,
      Yup.string().when(FORMIK_ADDRESS_FIELD_IDS.COUNTRY, (country, currSchema) =>
        currSchema.test(...getPostalCodeYupTestParams(false, country))
      )
    );

    if (gbgValidationStatus.is_address_validation_enabled) {
      addValidation(
        FORMIK_ADDRESS_FIELD_IDS.OVERRIDE_REASON,
        Yup.string().test('override-reason-required', 'Required for manually edited addresses', function (value) {
          const addressValues = this.parent;

          const country = addressValues[FORMIK_ADDRESS_FIELD_IDS.COUNTRY];
          if (!country || !isGbgSupportedCountry(country)) return true;

          const hasAddress = ADDRESS_FIELDS_TO_WATCH.some((field) => !!addressValues[field]?.trim());
          if (!hasAddress) return true;

          if (addressValues[FORMIK_ADDRESS_FIELD_IDS.ADDRESS_VALIDATION]) return true;

          const serverValidation = originalContact?.validations?.find(
            (v) => v.validation_type === ADDRESS_VALIDATION_TYPE
          );
          if (serverValidation && REVERTABLE_STATUSES.some((s) => s === serverValidation.status)) {
            const isDirty = ADDRESS_FIELDS_TO_WATCH.some(
              (field) => norm(addressValues[field]) !== norm(originalContact?.[field])
            );
            if (!isDirty) return true;
          }

          return !!value?.trim();
        })
      );
    }

    if (!isCreation) {
      addValidation(
        FORMIK_ADDRESS_FIELD_IDS.REASON,
        Yup.string().test(
          'address-reason',
          'Required',
          getReasonValidationSchema(originalContact, getValuesToCompare, getInitialValues)
        )
      );
      addValidation(
        FORMIK_ADDRESS_FIELD_IDS.EXPLANATION,
        Yup.string().when('reason', {
          is: 'other',
          then: Yup.string().required('Required'),
          otherwise: Yup.string(),
        })
      );
    }

    return validations;
  };

  const getValuesToCompare = (values: FormikValues) => {
    const omittedValues = omit(values, [
      FORMIK_ADDRESS_FIELD_IDS.REASON,
      FORMIK_ADDRESS_FIELD_IDS.EXPLANATION,
      FORMIK_ADDRESS_FIELD_IDS.ADDRESS_VALIDATION,
      FORMIK_ADDRESS_FIELD_IDS.OVERRIDE_REASON,
    ]);
    return getInitializedValues(omittedValues);
  };

  const getPreparedPayload = (values: FormikValues) => {
    const payload = { ...values };
    if (!payload[FORMIK_ADDRESS_FIELD_IDS.OVERRIDE_REASON]?.trim()) {
      payload[FORMIK_ADDRESS_FIELD_IDS.OVERRIDE_REASON] = null;
    }
    return payload;
  };

  return {
    label: 'Address',
    tabKey: ADDRESS_TAB_KEY,
    tabComponent: AddressTab,
    getInitialValues,
    getValidationSchema,
    getValuesToCompare,
    getPreparedPayload,
  };
};

export default useAddressTab;
